import { useMutation, useQuery, useQueryClient } from "react-query";
import qs from "qs-lite";
import api from "../../utils/api";
import { CUSTOMERS } from "./useCustomerData";
import { useHubSnackBar } from "../useSnackBar";
import { invalidate } from "../../helpers/clientHelper";
import { getQueryParams } from "../../helpers";

const PARTS = "/parts";

const getSearchPartList = ({ queryKey }) => {
  const query = qs.stringify(queryKey[1]);
  const path = queryKey[1] ? `${PARTS}/search?${query}` : `${PARTS}/search`;
  return api.get(path);
};
export const useGetSearchPartList = (search, partId) => {
  const query = {
    ...(search && { search }),
    ...(partId && { partId }),
  };
  return useQuery([PARTS, query], getSearchPartList, {
    enabled: !!search || !!partId,
  });
};

const getPartsByCustomerId = ({ queryKey }) =>
  api.get(`${PARTS}/customer/${queryKey[1]}`);
export const useGetCustomerParts = (id) => {
  return useQuery([PARTS, id, "Customer"], getPartsByCustomerId, {
    enabled: !!id,
  });
};

const getPartData = ({ queryKey }) => {
  const id = queryKey[1];
  const locationId = queryKey[2]?.locationId;
  const qsStr = locationId ? `?${qs.stringify({ locationId })}` : "";
  return api.get(`${PARTS}/${id}${qsStr}`);
};
export const useGetPartData = ({ id, locationId } = {}) => {
  return useQuery(
    [PARTS, id, { locationId: locationId || null }],
    getPartData,
    {
      enabled: !!id,
      staleTime: 300_000,
    },
  );
};

const getAllParts = ({ queryKey }) => {
  const queryString = qs.stringify(queryKey[1]);
  return api.get(`${PARTS}?${queryString}`);
};
export const useGetAllParts = () => {
  const queryProps = getQueryParams({ defaultOrderBy: "partName" });
  return useQuery([PARTS, { ...queryProps }], getAllParts);
};

const postCreatePart = (payload) => api.post(PARTS, payload);
export const usePostCreatePart = () => {
  const { addSnackbarSuccess } = useHubSnackBar();
  const queryClient = useQueryClient();

  return useMutation(postCreatePart, {
    onSuccess: () => {
      invalidate(queryClient, CUSTOMERS);
      addSnackbarSuccess({ message: "Successfully Created Contact" });
    },
  });
};

const putPartData = ({ id, ...payload }) => {
  return api.put(`${PARTS}/${id}`, payload);
};
export const usePutPartData = () => {
  const { addSnackbarSuccess } = useHubSnackBar();
  const queryClient = useQueryClient();

  return useMutation(putPartData, {
    onSuccess: (updated, variables) => {
      const id = variables?.id;
      const locationId = variables?.locationId || null;
      if (id && updated) {
        queryClient.setQueryData([PARTS, id, { locationId }], updated);
        queryClient.setQueryData(
          [`${PARTS}/instructions/${id}`, locationId],
          updated,
        );
      }
      addSnackbarSuccess({ message: "Successfully Updated Part" });
    },
  });
};

const postPartEstiamte = (payload) => api.post(`${PARTS}/estimate`, payload);
export const usePostPartEstimate = () => useMutation(postPartEstiamte);

const deletePartById = (id) => api.delete(`${PARTS}/${id}`);
export const useDeletePartById = () => {
  const { addSnackbarSuccess } = useHubSnackBar();
  const queryClient = useQueryClient();

  return useMutation(deletePartById, {
    onSuccess: () => {
      invalidate(queryClient, CUSTOMERS);
      addSnackbarSuccess({ message: "Successfully Deleted Part" });
    },
  });
};

const getPartHistory = ({ queryKey }) => {
  const queryString = qs.stringify(queryKey[1]);
  const id = queryKey[2];
  return api.get(`${PARTS}/history/${id}?${queryString}`);
};
export const useGetPartHistory = (id) => {
  const queryProps = getQueryParams({
    defaultOrderBy: "createdAt",
    defaultSortOrder: "desc",
  });
  return useQuery([PARTS, { ...queryProps }, id], getPartHistory);
};

const putPartRevertData = (data) => {
  const id = data.id;
  delete data.id;
  return api.put(`${PARTS}/revert/${id}`, data);
};
export const usePutPartRevertData = () => {
  const { addSnackbarSuccess } = useHubSnackBar();
  const queryClient = useQueryClient();

  return useMutation(putPartRevertData, {
    onSuccess: () => {
      invalidate(queryClient, PARTS);
      addSnackbarSuccess({ message: "Successfully Updated Part" });
    },
  });
};

const postMergePartData = (data) => {
  const id = data.id;
  delete data.id;
  return api.post(`${PARTS}/merge/${id}`, data);
};
export const usePostMergePartData = () => {
  const { addSnackbarSuccess } = useHubSnackBar();
  const queryClient = useQueryClient();

  return useMutation(postMergePartData, {
    onSuccess: () => {
      invalidate(queryClient, PARTS);
      addSnackbarSuccess({ message: "Successfully Merged Part" });
    },
  });
};
